import {
  AddIcon,
  ChevronDownIcon,
  DeleteIcon,
  EditIcon,
  SearchIcon,
} from "@chakra-ui/icons";

import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Avatar,
  Box,
  Button,
  Card,
  Center,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";

import {
  AddCustomer,
  Customer,
  customerRepository,
} from "api/customer";

import { useEffect, useRef, useState } from "react";

import AddCustomerDrawer from "./addcutomer";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import React from "react";


const PURPLE = "#7062B9";
const PURPLE_DARK = "#5B4FA3";
const PURPLE_SOFT = "#EEEBFA";
const NAVY = "#2D2A5E";
const MUTED = "#9A98B8";
const TEAL = "#2BA8A0";
const RED = "#E5484D";

export default function CustomerTable() {
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const centerAlertTimerRef = useRef<number | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [deleteId, setDeleteId] = useState<string>("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState<Customer[]>([]);
  const [form, setForm] = useState<AddCustomer>({
    customerName: "",
    email: "",
    gstIn: "",
    phoneNo: "",
    plant: "",
    address: "",
    equipmentName: "",
    amcAmount: "",
    isAMC: false,
    amcStartDate: "",
    amcEndDate: "",
  });

  /* ---------- Theme tokens ---------- */
  const pageBg = useColorModeValue("#F0F2FF", "#0B1437");
  const cardBg = useColorModeValue("white", "#111C44");
  const rowBg = useColorModeValue("white", "#1B254B");
  const rowAltBg = useColorModeValue("#FAF9FF", "#18224A");
  const textColor = useColorModeValue(NAVY, "white");
  const mutedColor = useColorModeValue(MUTED, "whiteAlpha.600");
  const rowShadow = useColorModeValue(
    "0 1px 3px rgba(112,98,185,0.10)",
    "0 1px 3px rgba(0,0,0,0.4)"
  );
  const btncolor = useColorModeValue(NAVY, "white");

  const showAlert = (
    status: "success" | "error" | "warning" | "info",
    message: string
  ) => {
    setCenterAlertStatus(status);
    setCenterAlertMessage(message);
    setCenterAlertVisible(true);

    if (centerAlertTimerRef.current) {
      clearTimeout(centerAlertTimerRef.current);
    }

    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
    }, 2000);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? target.checked
          : type === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
    }));
  };

  const handleReset = () => {
    setForm({
      customerName: "",
      plant: "",
      address: "",
      email: "",
      gstIn: "",
      phoneNo: "",
      equipmentName: "",
      amcAmount: "",
      isAMC: false,
      amcStartDate: "",
      amcEndDate: "",
    });

    setIsEdit(false);
    setEditingId("");
  };


  const fetchCustomer = async () => {
    setIsLoading(true);

    try {
      const response = await customerRepository.getcustomer(
        pageIndex + 1,
        pageSize,
        search
      );

      console.log("Pagination response:", response);

      setCustomer(
        Array.isArray(response.data) ? response.data : []
      );

      const total = response.totalCount ?? 0;
      setTotalCount(total);

      setHasMore((pageIndex + 1) * pageSize < total);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      showAlert("error", "Unable to fetch customers");
    } finally {
      setIsLoading(false);
    }
  };


  const handleEdit = (item: Customer) => {
    setIsEdit(true);
    setEditingId(item.id);

    setForm({
      customerName: item.customerName || "",
      plant: item.plant || "",
      address: item.address || "",
      email: item.email || "",
      gstIn: item.gstIn || "",
      phoneNo: item.phoneNo || "",
      equipmentName: item.equipmentName || "",
      amcAmount:
        item.amcAmount !== null && item.amcAmount !== undefined
          ? Number(item.amcAmount)
          : "",
      isAMC: item.isAMC === true,
      amcStartDate: item.amcStartDate ? item.amcStartDate.substring(0, 10) : "",
      amcEndDate: item.amcEndDate ? item.amcEndDate.substring(0, 10) : "",
    });

    onOpen();
  };

  const handleSubmit = async () => {
    try {
      if (form.isAMC === true && form.amcAmount === "") {
        showAlert("warning", "Please enter AMC Amount");
        return;
      }

      if (form.isAMC === true && !form.amcStartDate) {
        showAlert("warning", "Please select AMC Start Date");
        return;
      }

      if (form.isAMC === true && !form.amcEndDate) {
        showAlert("warning", "Please select AMC End Date");
        return;
      }

      const payload: AddCustomer = {
        customerName: form.customerName || "",
        plant: form.plant || "",
        address: form.address || "",
        email: form.email || "",
        gstIn: form.gstIn || "",
        phoneNo: form.phoneNo || "",
        equipmentName: form.equipmentName || "",
        amcAmount: form.amcAmount === "" ? 0 : Number(form.amcAmount),
        isAMC: Boolean(form.isAMC),
        amcStartDate:
          form.isAMC && form.amcStartDate ? form.amcStartDate : undefined,
        amcEndDate:
          form.isAMC && form.amcEndDate ? form.amcEndDate : undefined,
      };


      if (isEdit) {
        const response = await customerRepository.updatecustomer(
          editingId,
          payload
        );

        setCustomer((prev) =>
          prev.map((item) => (item.id === editingId ? response : item))
        );

        showAlert("success", "Customer Updated Successfully");
      } else {
        const response = await customerRepository.addcustomer(payload);

        setCustomer((prev) => [...prev, response]);

        showAlert("success", "Customer Added Successfully");
      }

      handleReset();
      onClose();
    } catch (error: any) {
      console.error("Customer save error:", error);
      console.error("Response:", error?.response?.data);

      const message = error?.response?.data?.errors
        ? JSON.stringify(error.response.data.errors)
        : error?.response?.data?.title ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to save Customer";

      showAlert("error", message);
    }
  };

  const handleDelete = async () => {
    try {
      await customerRepository.deletecustomer(deleteId);

      setCustomer((prev) => prev.filter((x) => x.id !== deleteId));

      showAlert("success", "Customer Deleted Successfully");

      setIsDeleteOpen(false);
      setDeleteId("");
    } catch (error: any) {
      console.error("Delete customer error:", error);

      const message =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message ||
          error?.response?.data?.title ||
          error?.message ||
          "Unable to delete customer";

      showAlert("error", message);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [pageIndex, pageSize, search]);

  const createPages = () => {
    const pages: number[] = [];

    for (let i = 0; i <= pageIndex; i++) {
      pages.push(i + 1);
    }

    if (hasMore) {
      pages.push(pageIndex + 2);
    }

    return pages;
  };

  const thStyle = {
    color: mutedColor,
    fontWeight: 500,
    fontSize: "12px",
    textTransform: "none" as const,
    letterSpacing: "0.02em",
    borderBottom: "none",
    py: 2,
  };


  const tdStyle = {
    border: "none",
    py: 4,
    fontSize: "14px",
    color: textColor,
    transition: "background 0.15s ease, color 0.15s ease",
  };

  return (
    <>
      {centerAlertVisible && (
        <Center position="fixed" inset={0} bg="blackAlpha.300" zIndex={9999}>
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="550px"
            borderRadius="18px"
            py={8}
            px={8}
            boxShadow="2xl"
          >
            <AlertIcon boxSize="44px" mr={0} />

            <Text mt={3} fontWeight="bold" fontSize="lg">
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

      <Flex mt="70px" p={2} bg={pageBg} borderRadius="25px">
        <Card
          bg={cardBg}
          borderRadius="25px"
          overflow="hidden"
          boxShadow="0 10px 30px rgba(112,98,185,0.12)"
          w="100%"
        >
          {/* ---------- Header ---------- */}
          <Flex p={6} justify="flex-end" align="center" gap={4} wrap="wrap">
            <HStack spacing={3}>
              <InputGroup maxW="320px">
                <InputLeftElement h="44px" pointerEvents="none">
                  <SearchIcon color={PURPLE} />
                </InputLeftElement>

                <Input
                  h="44px"
                  borderRadius="14px"
                  bg={PURPLE_SOFT}
                  border="none"
                  color={NAVY}
                  _placeholder={{ color: MUTED }}
                  _focus={{ boxShadow: `0 0 0 2px ${PURPLE}` }}
                  placeholder="Search customer..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPageIndex(0);
                  }}
                />
              </InputGroup>

              <Button
                leftIcon={<AddIcon boxSize="3" />}
                h="50px"
                minW="max-content"
                px={6}
                fontSize="md"
                whiteSpace="nowrap"
                flexShrink={0}
                borderRadius="14px"
                bg={PURPLE}
                color="white"
                _hover={{ bg: PURPLE_DARK }}
                _active={{ bg: PURPLE_DARK }}
                boxShadow="0 6px 14px rgba(112,98,185,0.35)"
                onClick={() => {
                  handleReset();
                  onOpen();
                }}
              >
                Add Customer
              </Button>
            </HStack>
          </Flex>

          {/* ---------- Table ---------- */}
          <Box px={6} pb={2} overflowX="auto">
            <Table
              variant="unstyled"
              sx={{ borderCollapse: "separate", borderSpacing: "0 10px" }}
            >
              <Thead>
                <Tr>
                  <Th {...thStyle}>Name</Th>
                  <Th {...thStyle}>GSTIN</Th>
                  <Th {...thStyle}>Contact</Th>
                  <Th {...thStyle}>Plant</Th>
                  <Th {...thStyle}>Equipment</Th>
                  <Th {...thStyle}>AMC</Th>
                  <Th {...thStyle} textAlign="right">
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {customer.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={10} border="none">
                      <Text color={mutedColor}>{isLoading ? "Loading..." : "No customers found"}</Text>
                    </Td>
                  </Tr>
                ) : (
                  customer.map((item: Customer, index: number) => {
                    const base = index % 2 === 0 ? rowBg : rowAltBg;

                    return (
                      <Tr
                        key={item.id}
                        cursor="pointer"
                        sx={{
                          "& > td": { bg: base, boxShadow: rowShadow },
                          "& > td:first-of-type": { borderLeftRadius: "14px" },
                          "& > td:last-of-type": { borderRightRadius: "14px" },
                        }}
                        _hover={{
                          "& > td": {
                            bg: `linear-gradient(90deg, ${PURPLE} 0%, #8A7DD0 100%)`,
                            color: "white",
                          },
                          "& .amc-pill": { bg: "whiteAlpha.300", color: "white" },
                          "& .row-action": { color: "white" },
                        }}
                      >
                        <Td {...tdStyle}>
                          <HStack spacing={3}>
                            <Avatar
                              size="sm"
                              name={item.customerName}
                              bg={PURPLE_SOFT}
                              color={PURPLE}
                            />
                            <Text fontWeight="600">{item.customerName}</Text>
                          </HStack>
                        </Td>

                        <Td {...tdStyle}>{item.gstIn}</Td>
                        <Td {...tdStyle}>{item.phoneNo}</Td>
                        <Td {...tdStyle}>{item.plant}</Td>
                        <Td {...tdStyle}>{item.equipmentName || "-"}</Td>

                        <Td {...tdStyle}>
                          <Box
                            className="amc-pill"
                            display="inline-block"
                            px={3}
                            py="2px"
                            borderRadius="full"
                            fontSize="12px"
                            fontWeight="600"
                            color={item.isAMC ? TEAL : MUTED}
                            bg={
                              item.isAMC
                                ? "rgba(43,168,160,0.12)"
                                : "rgba(154,152,184,0.16)"
                            }
                          >
                            {item.isAMC ? "AMC" : "Non-AMC"}
                          </Box>
                        </Td>

                        <Td {...tdStyle} textAlign="right">
                          <HStack justify="flex-end" spacing={1}>
                            <IconButton
                              className="row-action"
                              aria-label="Edit Customer"
                              size="sm"
                              variant="ghost"
                              color={PURPLE}
                              _hover={{ bg: "whiteAlpha.300" }}
                              icon={<EditIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(item);
                              }}
                            />

                            <IconButton
                              className="row-action"
                              aria-label="Delete Customer"
                              size="sm"
                              variant="ghost"
                              color={RED}
                              _hover={{ bg: "whiteAlpha.300" }}
                              icon={<DeleteIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(item.id);
                                setIsDeleteOpen(true);
                              }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          {/* ---------- Footer ---------- */}
          <Flex
            w="100%"
            justify="space-between"
            align="center"
            px={6}
            pb={6}
            pt={2}
            wrap="wrap"
            gap={3}
          >
            <HStack spacing={3}>
              <Menu>
                <Text fontSize="13px" color={mutedColor}>
                  Rows per page
                </Text>
                <MenuButton
                  as={Button}
                  size="sm"
                  rightIcon={<ChevronDownIcon />}
                  bg={PURPLE_SOFT}
                  color={PURPLE}
                  borderRadius="10px"
                  _hover={{ bg: "#E2DDF6" }}
                  _active={{ bg: "#E2DDF6" }}
                >
                  {pageSize}
                </MenuButton>

                <MenuList zIndex={1000} boxShadow="lg" borderRadius="12px">
                  {[10, 20].map((size) => (
                    <MenuItem
                      key={size}
                      onClick={() => {
                        setPageSize(size);
                        setPageIndex(0);
                      }}
                    >
                      {size}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </HStack>

            <HStack spacing={1}>
             
              <IconButton
                aria-label="Previous page"
                icon={<MdChevronLeft size={18} />}
                variant="ghost"
                size="sm"
                color={btncolor}
                isDisabled={isLoading || pageIndex === 0}
                onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
              />

              {createPages().map((num: number, index: number) => {
                const active = num === pageIndex + 1;
                return (
                  <Button
                    key={index}
                    size="sm"
                    minW="32px"
                    h="32px"
                    borderRadius="10px"
                    fontWeight={active ? 700 : 500}
                    bg={active ? PURPLE : "transparent"}
                    color={active ? "white" : btncolor}
                    _hover={{ bg: active ? PURPLE_DARK : PURPLE_SOFT }}
                    onClick={() => setPageIndex(num - 1)}
                  >
                    {num}
                  </Button>
                );
              })}

              <IconButton
                aria-label="Next page"
                icon={<MdChevronRight size={18} />}
                variant="ghost"
                size="sm"
                color={btncolor}
                isDisabled={isLoading || !hasMore}
                onClick={() => setPageIndex((prev) => prev + 1)}
              />
            </HStack>
          </Flex>
        </Card>

        <AddCustomerDrawer
          isOpen={isOpen}
          onClose={onClose}
          isEdit={isEdit}
          form={form}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          handleReset={handleReset}
        />

        <AlertDialog
          isOpen={isDeleteOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setIsDeleteOpen(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent borderRadius="18px">
              <AlertDialogHeader>Delete Customer</AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this customer?
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setIsDeleteOpen(false)}>
                  Cancel
                </Button>

                <Button colorScheme="red" ml={3} onClick={handleDelete}>
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Flex>
    </>
  );
}